import React, { useEffect, useMemo } from "react";

import { FieldSatelliteObservations } from "services";

import { formatDayDate, isNoSatelliteImageError, useGetFieldSatelliteImage, usePrefetchFieldSatelliteDay } from "utils";

import { CropFCPO } from "models";

import { Button, Icon, LazyLoadImage, Modal, Spin, Typography } from "components";

import { SatelliteChart } from "pages/admin/verifications/components/automations/SatelliteChart";

type SatelliteDayModalProps = {
  farmId: string;
  fieldId: string;
  fieldName: string;
  observations: FieldSatelliteObservations;
  crops: CropFCPO[];
  coverCropWindowStart?: string | null;
  date: string | null;
  onDateChange: (date: string | null) => void;
};

type DayImageState = { url?: string; error?: unknown };

const DayImage = ({ image: { url, error }, date, label }: { image: DayImageState; date: string; label: string }) => (
  <div className="satellite-day-image">
    <Typography variant="paragraph-s" color="grey-600">
      {label}
    </Typography>
    <div className="satellite-day-image-frame">
      {!url && !error && <Spin size="small" />}
      {!!error && (
        <Typography variant="paragraph-s" color="grey-600">
          {isNoSatelliteImageError(error) ? "No image for this day" : "This picture didn't load"}
        </Typography>
      )}
      {url && <LazyLoadImage src={url} alt={`${label}, ${formatDayDate(date)}`} width="100%" height="100%" visibleByDefault />}
    </div>
  </div>
);

/** One satellite pass: that exact day's natural colour and NDVI images, and where the day sits on the chart. */
export const SatelliteDayModal = ({ farmId, fieldId, fieldName, observations, crops, coverCropWindowStart, date, onDateChange }: SatelliteDayModalProps) => {
  const imagePasses = useMemo(() => observations.passes.filter(({ hasImage }) => hasImage), [observations.passes]);
  const index = imagePasses.findIndex((pass) => pass.date === date);
  const pass = imagePasses[index];
  const previous = imagePasses[index - 1];
  const next = imagePasses[index + 1];

  // On a first view one render makes both layers, so NDVI waits for natural colour instead of rendering again. A
  // failure other than "no picture that day" would fail NDVI the same way, so it isn't asked then.
  const trueColor = useGetFieldSatelliteImage({ farmId, fieldId, date: pass?.date ?? null, layer: "true-color" });
  const isNdviSkipped = trueColor.isError && !isNoSatelliteImageError(trueColor.error);
  const ndvi = useGetFieldSatelliteImage({
    farmId,
    fieldId,
    date: pass?.date ?? null,
    layer: "ndvi",
    enabled: trueColor.isSuccess || (trueColor.isError && !isNdviSkipped),
  });

  // Once this day is on screen, get the next one ready, so Next opens without waiting.
  const prefetchDay = usePrefetchFieldSatelliteDay();
  const isDayShown = trueColor.isSuccess && ndvi.isSuccess;
  useEffect(() => {
    if (isDayShown && next) prefetchDay({ farmId, fieldId, date: next.date });
  }, [isDayShown, next, farmId, fieldId, prefetchDay]);

  useEffect(() => {
    if (!date) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && previous) onDateChange(previous.date);
      if (event.key === "ArrowRight" && next) onDateChange(next.date);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [date, previous, next, onDateChange]);

  const facts = pass
    ? [
        pass.fieldCloudCoverPct !== null && `${pass.fieldCloudCoverPct}% cloud over the field`,
        pass.ndvi !== null && `NDVI ${pass.ndvi.toFixed(2)}`,
        pass.radarVhDb !== null && `Radar ${pass.radarVhDb.toFixed(1)} dB`,
        `picture ${index + 1} of ${imagePasses.length}`,
      ].filter(Boolean)
    : [];

  return (
    <Modal
      destroyOnClose
      maskClosable
      showModal={!!pass}
      onCancel={() => onDateChange(null)}
      closeIcon={<Icon iconName="tabler/x" />}
      title={pass ? `${fieldName} · ${formatDayDate(pass.date)}` : ""}
      wrapClassName="satellite-day-modal-wrap"
      footer={
        <div className="satellite-day-modal-footer">
          <Button
            type="secondary"
            iconLeft
            icon={<Icon iconName="tabler/chevron-left" size="s" />}
            disabled={!previous}
            onClick={() => previous && onDateChange(previous.date)}
          >
            Previous
          </Button>
          <Button type="secondary" icon={<Icon iconName="tabler/chevron-right" size="s" />} disabled={!next} onClick={() => next && onDateChange(next.date)}>
            Next
          </Button>
          <Button type="primary" className="satellite-day-modal-close" onClick={() => onDateChange(null)}>
            Close
          </Button>
        </div>
      }
      content={
        pass && (
          <div className="satellite-day-modal">
            <Typography variant="paragraph-m" color="grey-600">
              Sentinel-2 pass · {facts.join(" · ")}
            </Typography>
            <div className="satellite-day-images">
              <DayImage image={{ url: trueColor.data?.url, error: trueColor.error }} date={pass.date} label="Natural colour" />
              {/* NDVI isn't asked after a failed natural colour, so it shows the same failure. */}
              <DayImage image={{ url: ndvi.data?.url, error: ndvi.error ?? (isNdviSkipped ? trueColor.error : undefined) }} date={pass.date} label="NDVI" />
            </div>
            <SatelliteChart
              observations={observations}
              crops={crops}
              coverCropWindowStart={coverCropWindowStart}
              markerDate={pass.date}
              height={220}
              onPassClick={onDateChange}
            />
          </div>
        )
      }
    />
  );
};
